import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Modal, Spinner } from 'react-bootstrap'
import clsx from 'clsx'
import { useSearchParams } from 'react-router-dom'
import { useIntl } from 'react-intl'
import { toast } from 'react-toastify'
import StickyLanguageSelector from '../../components/StickyLanguageSelector'
import {
  createOne,
  deleteOne,
  getAll,
  patchOne,
  updateOne,
} from '../../services/common_requests'
import { KTCardBody } from '../../../_dashboard/helpers'
import MenuItemEditModal from './MenuItemEditModal'
import HeaderSiteSettingsSection from './HeaderSiteSettingsSection'
import {
  MenuItem,
  MenuLocation,
  MENU_LOCATIONS,
  MENU_NAV_LANGUAGES,
  displayLabel,
  getSiblings,
  isExternalUrl,
  parentKey,
} from './menuNavigationUtils'

const I18N_CONFIG_KEY = process.env.REACT_APP_I18N_CONFIG_KEY || 'i18nConfig'

const LOCATION_PILL_VARIANT: Record<MenuLocation, string> = {
  main: 'btn-light-primary',
  footer_col1: 'btn-light-info',
  footer_col2: 'btn-light-success',
  footer_col3: 'btn-light-warning',
  mobile: 'btn-light-secondary',
}

function readInitialLang(): string {
  try {
    const ls = localStorage.getItem(I18N_CONFIG_KEY) || '{"selectedLang":"fr"}'
    const code = JSON.parse(ls).selectedLang as string
    return (MENU_NAV_LANGUAGES as readonly string[]).includes(code) ? code : 'en'
  } catch {
    return 'en'
  }
}

function extractList(res: any): MenuItem[] {
  if (!res) return []
  if (Array.isArray(res.data)) return res.data as MenuItem[]
  return []
}

function nextPositionFor(
  all: MenuItem[],
  location: MenuLocation,
  parent: string | null,
): number {
  const pk = parentKey(parent)
  const sibs = all.filter(
    (m) => m.location === location && parentKey(m.parent as string | null) === pk,
  )
  return sibs.length ? Math.max(...sibs.map((s) => s.position)) + 1 : 0
}

const MenuNavigationManagement: React.FC = () => {
  const intl = useIntl()
  const [searchParams] = useSearchParams()
  const [mainTab, setMainTab] = useState<'menu' | 'header'>('menu')
  const [selectedLanguage, setSelectedLanguage] = useState(readInitialLang)
  const [locationTab, setLocationTab] = useState<MenuLocation>('main')
  const [items, setItems] = useState<MenuItem[]>([])
  const [pagesLoaded, setPagesLoaded] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<MenuItem | null>(null)
  const [saving, setSaving] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<MenuItem | null>(null)
  const [deleting, setDeleting] = useState(false)

  const loadMenu = useCallback(async () => {
    setLoading(true)
    try {
      const res = await getAll({}, '/menu')
      setItems(extractList(res))
    } catch (e) {
      console.error(e)
      toast.error(intl.formatMessage({ id: 'MENU_NAVIGATION.ERROR.LOAD' }))
    } finally {
      setLoading(false)
    }
  }, [intl])

  useEffect(() => {
    loadMenu()
  }, [loadMenu])

  useEffect(() => {
    const loc = searchParams.get('location')
    if (loc && (MENU_LOCATIONS as readonly string[]).includes(loc)) {
      setLocationTab(loc as MenuLocation)
      setMainTab('menu')
    }
    if (searchParams.get('tab') === 'header') {
      setMainTab('header')
    }
  }, [searchParams])

  useEffect(() => {
    getAll({}, '/pages')
      .then((res) => {
        const list = extractList(res) as any[]
        const urls = list
          .map((p) => {
            const slug = (p.slug || '').toString().replace(/^\/+/, '')
            return slug ? `/${slug}` : '/'
          })
          .filter(Boolean)
        setPagesLoaded(Array.from(new Set(urls)))
      })
      .catch(() => setPagesLoaded([]))
  }, [])

  const rows = useMemo(() => {
    const filtered = items.filter((i) => i.location === locationTab)
    return [...filtered].sort((a, b) => {
      const pa = parentKey(a.parent as string | null)
      const pb = parentKey(b.parent as string | null)
      if (pa !== pb) return pa.localeCompare(pb)
      return a.position - b.position
    })
  }, [items, locationTab])

  const parentLabel = useCallback(
    (parentId: string | null | undefined) => {
      if (parentId == null || parentId === '') {
        return intl.formatMessage({ id: 'MENU_NAVIGATION.TABLE.PARENT_ROOT' })
      }
      const p = items.find((i) => i._id === String(parentId))
      return p ? displayLabel(p, selectedLanguage) : '—'
    },
    [items, selectedLanguage, intl],
  )

  const handleReorder = async (item: MenuItem, direction: 'up' | 'down') => {
    const siblings = getSiblings(items, item)
    const idx = siblings.findIndex((s) => s._id === item._id)
    if (idx < 0) return
    const j = direction === 'up' ? idx - 1 : idx + 1
    if (j < 0 || j >= siblings.length) return
    const reordered = [...siblings]
    const tmp = reordered[idx]!
    reordered[idx] = reordered[j]!
    reordered[j] = tmp
    const payload = reordered.map((s, i) => ({ id: s._id, position: i }))
    try {
      const res = await patchOne({ items: payload }, '/menu/reorder')
      if ((res as any)?.payload?.code === 3003) {
        toast.success(intl.formatMessage({ id: 'MENU_NAVIGATION.SUCCESS.REORDER' }))
        loadMenu()
      } else {
        toast.error(intl.formatMessage({ id: 'MENU_NAVIGATION.ERROR.REORDER' }))
      }
    } catch (e) {
      toast.error(intl.formatMessage({ id: 'MENU_NAVIGATION.ERROR.REORDER' }))
    }
  }

  const handleSaveModal = async (values: {
    label: string
    url: string
    parent: string | null
    location: MenuLocation
    openInNewTab: boolean
    icon: string
    isActive: boolean
    translationsPayload: { language: string; label: string }[]
  }) => {
    setSaving(true)
    try {
      const base = {
        label: values.label,
        url: values.url,
        target: values.openInNewTab ? ('_blank' as const) : ('_self' as const),
        parent: values.parent,
        location: values.location,
        isActive: values.isActive,
        icon: values.icon || undefined,
        openInNewTab: values.openInNewTab,
        translations: values.translationsPayload,
      }
      if (editing) {
        const res = await updateOne(base, `/menu/${editing._id}`)
        if ((res as any)?.payload?.code === 3004) {
          toast.success(intl.formatMessage({ id: 'MENU_NAVIGATION.SUCCESS.UPDATE' }))
          setModalOpen(false)
          setEditing(null)
          loadMenu()
        } else {
          toast.error(intl.formatMessage({ id: 'MENU_NAVIGATION.ERROR.SAVE' }))
        }
      } else {
        const pos = nextPositionFor(items, values.location, values.parent)
        const res = await createOne({ ...base, position: pos }, '/menu')
        if ((res as any)?.payload?.code === 3002) {
          toast.success(intl.formatMessage({ id: 'MENU_NAVIGATION.SUCCESS.CREATE' }))
          setModalOpen(false)
          setEditing(null)
          loadMenu()
        } else {
          toast.error(intl.formatMessage({ id: 'MENU_NAVIGATION.ERROR.SAVE' }))
        }
      }
    } catch (e) {
      console.error(e)
      toast.error(intl.formatMessage({ id: 'MENU_NAVIGATION.ERROR.SAVE' }))
    } finally {
      setSaving(false)
    }
  }

  const confirmDelete = async () => {
    if (!deleteTarget) return
    setDeleting(true)
    try {
      await deleteOne(deleteTarget._id, '/menu')
      toast.success(intl.formatMessage({ id: 'MENU_NAVIGATION.SUCCESS.DELETE' }))
      setDeleteTarget(null)
      loadMenu()
    } catch (e) {
      toast.error(intl.formatMessage({ id: 'MENU_NAVIGATION.ERROR.DELETE' }))
    } finally {
      setDeleting(false)
    }
  }

  return (
    <>
      <KTCardBody className='py-6 px-4 px-lg-8'>
        <div className='d-flex flex-column mb-8'>
          <h1 className='fs-2x fw-bold text-gray-900 mb-1'>
            {intl.formatMessage({ id: 'MENU_NAVIGATION.PAGE_TITLE' })}
          </h1>
          <p className='text-gray-500 fs-6 mb-0'>
            {intl.formatMessage({ id: 'MENU_NAVIGATION.PAGE_SUBTITLE' })}
          </p>
        </div>

        <ul className='nav nav-tabs nav-line-tabs nav-line-tabs-2x mb-6 fs-6 border-bottom border-gray-300'>
          <li className='nav-item'>
            <button
              type='button'
              className={clsx(
                'nav-link text-active-primary fw-semibold pb-3 px-0 me-8 bg-transparent border-0',
                mainTab === 'menu' && 'active',
              )}
              onClick={() => setMainTab('menu')}
            >
              <i className='bi bi-list fs-4 me-2' />
              {intl.formatMessage({ id: 'MENU_NAVIGATION.TAB_MENU' })}
            </button>
          </li>
          <li className='nav-item'>
            <button
              type='button'
              className={clsx(
                'nav-link text-active-primary fw-semibold pb-3 px-0 bg-transparent border-0',
                mainTab === 'header' && 'active',
              )}
              onClick={() => setMainTab('header')}
            >
              <i className='bi bi-image fs-4 me-2' />
              {intl.formatMessage({ id: 'MENU_NAVIGATION.TAB_HEADER_SETTINGS' })}
            </button>
          </li>
        </ul>

        {mainTab === 'header' && <HeaderSiteSettingsSection />}

        {mainTab === 'menu' && (
          <>
            <StickyLanguageSelector
              variant='compact'
              selectedLanguage={selectedLanguage}
              onLanguageChange={setSelectedLanguage}
              allowedLanguageCodes={[...MENU_NAV_LANGUAGES]}
            />

            <div className='d-flex flex-column flex-lg-row flex-lg-wrap justify-content-between align-items-start align-items-lg-center gap-4 mb-6'>
              <ul className='nav nav-pills nav-pills-custom gap-2 flex-nowrap mb-0'>
                {MENU_LOCATIONS.map((loc) => (
                  <li className='nav-item' key={loc}>
                    <button
                      type='button'
                      className={clsx(
                        'nav-link btn btn-sm btn-flex fw-bold rounded-pill px-4 transition-all',
                        LOCATION_PILL_VARIANT[loc],
                        locationTab === loc ? 'active shadow-sm' : 'text-gray-700',
                      )}
                      onClick={() => setLocationTab(loc)}
                    >
                      <i className='bi bi-menu-button-wide me-2 fs-7' />
                      {intl.formatMessage({ id: `MENU_NAVIGATION.LOCATION.${loc}` })}
                    </button>
                  </li>
                ))}
              </ul>
              <button
                type='button'
                className='btn btn-primary btn-sm d-flex align-items-center gap-2 rounded-pill shadow-sm'
                onClick={() => {
                  setEditing(null)
                  setModalOpen(true)
                }}
              >
                <i className='bi bi-plus-lg' />
                <span>{intl.formatMessage({ id: 'MENU_NAVIGATION.BTN_ADD' })}</span>
              </button>
            </div>

            {loading ? (
              <div className='text-center py-15'>
                <Spinner animation='border' className='text-primary' />
              </div>
            ) : (
              <div className='card card-flush shadow-sm'>
                <div className='card-body p-0'>
                  <div className='table-responsive'>
                    <table className='table table-hover align-middle table-row-dashed table-row-gray-200 fs-6 gy-4 mb-0'>
                      <thead>
                        <tr className='fw-semibold text-gray-500 text-uppercase fs-7 border-bottom border-gray-200'>
                          <th className='ps-6 w-60px min-w-60px'>
                            {intl.formatMessage({ id: 'MENU_NAVIGATION.COL.POSITION' })}
                          </th>
                          <th className='min-w-150px'>
                            {intl.formatMessage({ id: 'MENU_NAVIGATION.COL.LABEL' })}
                          </th>
                          <th className='min-w-120px'>
                            {intl.formatMessage({ id: 'MENU_NAVIGATION.COL.URL' })}
                          </th>
                          <th className='w-100px'>
                            {intl.formatMessage({ id: 'MENU_NAVIGATION.COL.TYPE' })}
                          </th>
                          <th className='w-120px'>
                            {intl.formatMessage({ id: 'MENU_NAVIGATION.COL.PARENT' })}
                          </th>
                          <th className='w-80px text-center'>
                            {intl.formatMessage({ id: 'MENU_NAVIGATION.COL.ACTIVE' })}
                          </th>
                          <th className='w-120px text-end pe-6'>
                            {intl.formatMessage({ id: 'COLUMNS.ACTIONS' })}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.length === 0 && (
                          <tr>
                            <td colSpan={7} className='text-center text-gray-500 py-12 fs-6'>
                              {intl.formatMessage({ id: 'MENU_NAVIGATION.EMPTY' })}
                            </td>
                          </tr>
                        )}
                        {rows.map((row) => {
                          const sibs = getSiblings(items, row)
                          const sIdx = sibs.findIndex((s) => s._id === row._id)
                          const ext = isExternalUrl(row.url)
                          return (
                            <tr
                              key={row._id}
                              className='border-bottom border-gray-200 transition-all'
                            >
                              <td className='ps-6'>
                                <div className='d-flex flex-column align-items-center gap-1 py-2'>
                                  <button
                                    type='button'
                                    className='btn btn-icon btn-sm btn-light-primary rounded-circle'
                                    onClick={() => handleReorder(row, 'up')}
                                    disabled={sIdx <= 0}
                                    title='Up'
                                  >
                                    <i className='bi bi-chevron-up fs-7' />
                                  </button>
                                  <span className='text-gray-600 fw-semibold fs-7'>
                                    {row.position}
                                  </span>
                                  <button
                                    type='button'
                                    className='btn btn-icon btn-sm btn-light-primary rounded-circle'
                                    onClick={() => handleReorder(row, 'down')}
                                    disabled={sIdx >= sibs.length - 1}
                                    title='Down'
                                  >
                                    <i className='bi bi-chevron-down fs-7' />
                                  </button>
                                </div>
                              </td>
                              <td>
                                <span className='text-gray-800 fw-bold fs-6'>
                                  {displayLabel(row, selectedLanguage)}
                                </span>
                              </td>
                              <td>
                                <code className='text-primary bg-light-primary px-2 py-1 rounded fs-7'>
                                  {row.url}
                                </code>
                              </td>
                              <td>
                                <span
                                  className={clsx(
                                    'badge fw-semibold fs-7',
                                    ext ? 'badge-light-warning' : 'badge-light-success',
                                  )}
                                >
                                  {ext
                                    ? intl.formatMessage({ id: 'MENU_NAVIGATION.TYPE.EXTERNAL' })
                                    : intl.formatMessage({ id: 'MENU_NAVIGATION.TYPE.INTERNAL' })}
                                </span>
                              </td>
                              <td>
                                <span className='text-gray-500 fs-7'>
                                  {parentLabel(row.parent as string | null)}
                                </span>
                              </td>
                              <td className='text-center'>
                                <span
                                  className={clsx(
                                    'badge rounded-pill px-3 fw-semibold fs-7',
                                    row.isActive ? 'badge-light-success' : 'badge-light-danger',
                                  )}
                                >
                                  {row.isActive
                                    ? intl.formatMessage({ id: 'STATUS.ACTIVE' })
                                    : intl.formatMessage({ id: 'STATUS.INACTIVE' })}
                                </span>
                              </td>
                              <td className='text-end pe-6'>
                                <div className='d-flex justify-content-end gap-2'>
                                  <button
                                    type='button'
                                    className='btn btn-icon btn-sm btn-light-primary rounded-circle transition-all'
                                    title={intl.formatMessage({ id: 'ACTION.EDIT' })}
                                    onClick={() => {
                                      setEditing(row)
                                      setModalOpen(true)
                                    }}
                                  >
                                    <i className='bi bi-pencil fs-7' />
                                  </button>
                                  <button
                                    type='button'
                                    className='btn btn-icon btn-sm btn-light-danger rounded-circle transition-all'
                                    title={intl.formatMessage({ id: 'ACTION.DELETE' })}
                                    onClick={() => setDeleteTarget(row)}
                                  >
                                    <i className='bi bi-trash fs-7' />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </KTCardBody>

      <MenuItemEditModal
        show={modalOpen}
        onHide={() => {
          setModalOpen(false)
          setEditing(null)
        }}
        item={editing}
        selectedLanguage={selectedLanguage}
        allItems={items}
        defaultLocation={locationTab}
        pageUrlSuggestions={pagesLoaded}
        onSubmit={handleSaveModal}
        saving={saving}
      />

      <Modal
        show={!!deleteTarget}
        onHide={() => !deleting && setDeleteTarget(null)}
        centered
        contentClassName='rounded-4 shadow'
      >
        <Modal.Header closeButton className='border-0 pb-0'>
          <Modal.Title className='fs-4 fw-bold'>
            {intl.formatMessage({ id: 'MENU_NAVIGATION.DELETE.TITLE' })}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className='pt-2 text-gray-700'>
          {intl.formatMessage({ id: 'MENU_NAVIGATION.DELETE.BODY' })}
        </Modal.Body>
        <Modal.Footer className='border-0 pt-0'>
          <Button
            variant='light'
            className='rounded-pill px-4'
            onClick={() => setDeleteTarget(null)}
            disabled={deleting}
          >
            {intl.formatMessage({ id: 'ACTION.CANCEL' })}
          </Button>
          <Button variant='danger' className='rounded-pill px-4' onClick={confirmDelete} disabled={deleting}>
            {deleting ? <Spinner size='sm' animation='border' className='me-2' /> : null}
            {intl.formatMessage({ id: 'ACTION.DELETE' })}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default MenuNavigationManagement
